import { createApi } from '@reduxjs/toolkit/query/react';
import { authQuery } from '../config/query.config';
import { cookieService } from '@lib/cookie.service';
import { IPublicProfile, setUser } from '../features/app';
import {
  IUserResponse,
  IUserSettingsPatch,
  ICreatorPublicProfileResponse,
  IAllFansByCreatorResponse,
  ICrewMembersQuery,
  ICrewMembersResponse,
  IRemoveCrewMemberPayload,
  IReinstateCrewMemberPayload,
} from './types';
import { QUERY } from '@constants/query.constants';
import {
  ISocialProfilePayload,
  ISocialProfileResponse,
} from 'app/(app)/dashboard/settings/_components/bio-social-settings/bio-social-settings.typings';
import { IGetFanProfileResponse } from 'app/(app)/fan-profile/fan-profile.typings';
import { IImageUploadResponse } from './types';
import { ICollaborator, ICreateTeamMemberResponse, IUser } from '@ui/components';
import { conversationsApi } from './conversations-api';

export const userApi = createApi({
  reducerPath: 'user-api',
  baseQuery: authQuery,
  tagTypes: [QUERY.user, QUERY.teams, QUERY.collaborators, QUERY.crewMembers],
  endpoints: (builder) => ({
    getUser: builder.query<IUser, void>({
      query: () => {
        // TODO: prevent undefined
        const userId = cookieService.getUserId();
        if (!userId) {
          return {
            url: '',
          };
        }
        return {
          url: '/users/' + userId,
        };
      },
      transformResponse: (data: IUserResponse) => {
        return {
          ...data,
          avatar: data.avatarURL,
          name: data.companyName,
          displayName: data.displayName,
          email: data.email,
          banner: data.imageURL,
          id: data._id,
        };
      },
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled;
          dispatch(setUser(data));
        } catch (error) {
          return;
        }
      },
      providesTags: [QUERY.user],
    }),
    getPublicProfile: builder.query<IPublicProfile, string>({
      query: (id) => {
        return {
          url: `/users-public-info/${id}`,
        };
      },
      transformResponse: (data: ICreatorPublicProfileResponse) => {
        return {
          avatar: data.avatarURL,
          name: data.companyName,
          displayName: data.displayName,
          banner: data.imageURL,
        };
      },
      providesTags: (_result, _error, id) => [{ type: QUERY.user, id }],
    }),
    createSocialProfile: builder.mutation<void, ISocialProfilePayload>({
      query(body) {
        return {
          url: '/social-profile',
          method: 'POST',
          body,
        };
      },
      invalidatesTags: [QUERY.user],
    }),
    updateSocialProfile: builder.mutation<void, Partial<ISocialProfilePayload>>({
      query(body) {
        return {
          url: '/social-profile',
          method: 'PATCH',
          body,
        };
      },
      invalidatesTags: [QUERY.user],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          userApi.util.updateQueryData('getSocialProfile', undefined, (draft) => {
            if (!draft?.socialProfile) {
              return;
            }

            const { platform_settings, ...socialProfile } = args;
            Object.assign(draft.socialProfile, socialProfile);

            if (platform_settings) {
              draft.socialProfile.platform_settings = {
                ...draft.socialProfile.platform_settings,
                ...platform_settings,
              };
            }
          }),
        );

        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    getSocialProfile: builder.query<ISocialProfileResponse | null, string | void>({
      query(creatorId) {
        const userId = creatorId || cookieService.getUserId();
        return {
          url: `/social-profile/${userId}`,
          method: 'GET',
        };
      },
      transformResponse(response: ISocialProfileResponse | null) {
        return response;
      },
      providesTags: [QUERY.user],
    }),
    updatePassword: builder.mutation({
      query({ oldPassword, newPassword }) {
        const userId = cookieService.getUserId();
        return {
          url: `/users/password-reset`,
          method: 'PUT',
          body: {
            id: userId,
            old_password: oldPassword,
            new_password: newPassword,
          },
        };
      },
    }),
    postImage: builder.mutation<string, { height: number; width: number; image: File }>({
      query: ({ height, width, image }) => {
        const form = new FormData();
        form.append('file', image);
        return {
          url: `/image-upload/?height=${height}&width=${width}&double_image=false`,
          method: 'POST',
          body: form,
          formData: true,
        };
      },
      transformResponse(data: IImageUploadResponse) {
        return data.location;
      },
    }),
    updateUserSettings: builder.mutation({
      query(payload: IUserSettingsPatch) {
        const userId = cookieService.getUserId();
        return {
          url: `/update-user-settings/${userId}`,
          method: 'PATCH',
          body: {
            id: userId,
            ...payload,
          },
        };
      },
      invalidatesTags: [QUERY.user],
      async onQueryStarted(args, { dispatch, queryFulfilled }) {
        const patchResult = args.membershipSettings
          ? dispatch(
              userApi.util.updateQueryData('getUser', undefined, (draft) => {
                draft.membershipSettings = args.membershipSettings;
              }),
            )
          : null;
        try {
          await queryFulfilled;
          if (args.avatarURL) {
            dispatch(
              conversationsApi.util.invalidateTags([
                QUERY.conversations,
                QUERY.communityConversations,
                QUERY.singleConversation,
              ]),
            );
          }
        } catch (error) {
          patchResult?.undo();
        }
      },
    }),
    generateCSVReport: builder.mutation<void, void>({
      query() {
        const userId = cookieService.getUserId();

        return {
          url: `/public/v1/csv-of-fans/${userId}`,
          method: 'GET',
        };
      },
    }),
    // TODO: move to ai-api after release
    initDataAssistant: builder.query<void, void>({
      query() {
        const userId = cookieService.getUserId();

        return {
          url: `/generate-ai-data/${userId}?badges=true`,
          method: 'POST',
        };
      },
    }),
    getFanProfile: builder.query<IGetFanProfileResponse, string>({
      query(fanId) {
        const userId = cookieService.getUserId();

        return {
          url: `/get-fan-profile/${fanId}?author=${userId}`,
        };
      },
      providesTags: (_result, _error, fanId) => [{ type: QUERY.user, id: fanId }],
    }),
    getTeamMembers: builder.query<IUser[], void>({
      query() {
        return {
          url: '/teams',
        };
      },
      providesTags: [QUERY.teams],
    }),
    createTeamMembers: builder.mutation<ICreateTeamMemberResponse[], string[]>({
      query(emails) {
        return {
          url: 'teams',
          method: 'POST',
          body: {
            emails,
          },
        };
      },
      invalidatesTags: [QUERY.teams],
      transformResponse(response: { results: ICreateTeamMemberResponse[] }) {
        return response.results;
      },
    }),
    deleteTeamMembers: builder.mutation<void, string[]>({
      query(emails) {
        return {
          url: 'teams',
          method: 'DELETE',
          body: {
            emails,
          },
        };
      },
      invalidatesTags: [QUERY.teams],
    }),
    getCollaborators: builder.query<ICollaborator[], void>({
      query() {
        return {
          url: '/users/collaborators',
        };
      },
      providesTags: [QUERY.collaborators],
    }),
    getAllFansByCreator: builder.query<
      IAllFansByCreatorResponse,
      { page?: number; itemsPerPage?: number; search?: string }
    >({
      query(params) {
        const userId = cookieService.getUserId();
        const { page = 1, itemsPerPage = 10, search } = params;

        let url = `/public/v1/all_fans_by_creator/${userId}?page=${page}&items_per_page=${itemsPerPage}`;

        if (search) {
          url += `&search=${encodeURIComponent(search)}`;
        }

        return {
          url,
          method: 'GET',
        };
      },
    }),
    getCrewMembers: builder.query<ICrewMembersResponse, ICrewMembersQuery>({
      query(params) {
        const creatorId = params.creatorId || cookieService.getUserId();
        const { page = 1, itemsPerPage = 10, search, status = 'active' } = params;
        const queryParams = new URLSearchParams({
          page: String(page),
          items_per_page: String(itemsPerPage),
        });

        if (search) {
          queryParams.set('search', search);
        }
        queryParams.set('status', status);

        return {
          url: `/public/v1/crew_memberships/${encodeURIComponent(
            String(creatorId),
          )}/members?${queryParams.toString()}`,
          method: 'GET',
        };
      },
      providesTags: [{ type: QUERY.crewMembers, id: 'LIST' }],
    }),
    removeCrewMember: builder.mutation<unknown, IRemoveCrewMemberPayload>({
      query({ creatorId, target_email, reason, internal_note, idempotencyKey }) {
        const resolvedCreatorId = creatorId || cookieService.getUserId();

        return {
          url: `/public/v1/crew_memberships/${encodeURIComponent(
            String(resolvedCreatorId),
          )}/remove`,
          method: 'POST',
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
          body: {
            target_email,
            reason,
            ...(internal_note ? { internal_note } : {}),
          },
        };
      },
      invalidatesTags: [{ type: QUERY.crewMembers, id: 'LIST' }],
    }),
    reinstateCrewMember: builder.mutation<unknown, IReinstateCrewMemberPayload>({
      query({ creatorId, target_email, idempotencyKey }) {
        const resolvedCreatorId = creatorId || cookieService.getUserId();

        return {
          url: `/public/v1/crew_memberships/${encodeURIComponent(
            String(resolvedCreatorId),
          )}/reinstate`,
          method: 'POST',
          headers: {
            'Idempotency-Key': idempotencyKey,
          },
          body: {
            target_email,
          },
        };
      },
      invalidatesTags: [{ type: QUERY.crewMembers, id: 'LIST' }],
    }),
  }),
});

export const {
  useGetUserQuery,
  useGetPublicProfileQuery,
  useUpdateUserSettingsMutation,
  useUpdatePasswordMutation,
  useGenerateCSVReportMutation,
  useInitDataAssistantQuery,
  useGetSocialProfileQuery,
  useCreateSocialProfileMutation,
  useUpdateSocialProfileMutation,
  useGetFanProfileQuery,
  usePostImageMutation,
  useGetTeamMembersQuery,
  useCreateTeamMembersMutation,
  useDeleteTeamMembersMutation,
  useGetCollaboratorsQuery,
  useGetAllFansByCreatorQuery,
  useGetCrewMembersQuery,
  useLazyGetCrewMembersQuery,
  useRemoveCrewMemberMutation,
  useReinstateCrewMemberMutation,
} = userApi;
